import React, { useState } from 'react';
import { formaterPrix } from '../../utils/formatPrix';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, Star, Search, Filter } from 'lucide-react';
import { API_URL } from '../../config';
import { CapitalFiltersState, Period } from './CapitalFilters';
import './MobileCapital.css';

interface IStock {
    id: number;
    name: string;
    symbol: string;
    amount: string;
    change: string;
    status: number;
    currency: string;
    lineChartData: number[];
    variation_pct: number;
    sector?: string;
    volume_montant?: number;
}

interface MobileCapitalProps {
    data: IStock[];
    keyword: string;
    setKeyword: (v: string) => void;
    filters: CapitalFiltersState;
    setFilters: (f: CapitalFiltersState) => void;
    favorites: Set<string>;
    onToggleFavorite: (symbol: string, isFav: boolean) => void;
    sectors: string[];
    getVariationLabel: () => string;
    totalCount: number;
}

const PERIOD_CHIPS: { value: Period; label: string }[] = [
    { value: 'day', label: "Jour" },
    { value: 'last_5', label: '5J' },
    { value: 'last_week', label: 'Sem' },
    { value: 'last_month', label: 'Mois' },
    { value: 'ytd', label: 'YTD' },
    { value: 'last_12_months', label: '12M' },
];

const MobileCapital: React.FC<MobileCapitalProps> = ({
    data,
    keyword,
    setKeyword,
    filters,
    setFilters,
    favorites,
    onToggleFavorite,
    sectors,
    getVariationLabel,
    totalCount
}) => {
    const navigate = useNavigate();
    const [showFilters, setShowFilters] = useState(false);
    const [imgErrors, setImgErrors] = useState<Set<string>>(new Set());

    const handleFav = async (e: React.MouseEvent, symbol: string, isFav: boolean) => {
        e.stopPropagation();
        const token = localStorage.getItem('access_token');
        if (!token) return;
        try {
            await fetch(`${API_URL}/api/favorites/${symbol}`, {
                method: isFav ? 'DELETE' : 'POST',
                headers: { Authorization: `Bearer ${token}` },
            });
            onToggleFavorite(symbol, !isFav);
        } catch (err) {
            console.error('Favorite toggle error:', err);
        }
    };

    const updateFilter = <K extends keyof CapitalFiltersState>(key: K, value: CapitalFiltersState[K]) => {
        setFilters({ ...filters, [key]: value });
    };

    const activeFilterCount = [
        filters.priceRange !== 'all',
        filters.variation !== 'all',
        filters.sector !== 'all',
        filters.amountRange !== 'all',
        filters.favoritesOnly
    ].filter(Boolean).length;

    return (
        <div className="mc-container">

            {/* Search bar */}
            <div className="mc-search-wrap">
                <Search size={16} className="mc-search-icon" />
                <input
                    type="text"
                    placeholder="Rechercher une action..."
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    className="mc-search-input"
                />
            </div>

            {/* Period chips — horizontal scroll */}
            <div className="mc-period-chips">
                {PERIOD_CHIPS.map(p => (
                    <button
                        key={p.value}
                        className={`mc-period-chip ${filters.period === p.value ? 'active' : ''}`}
                        onClick={() => updateFilter('period', p.value)}
                    >
                        {p.label}
                    </button>
                ))}
            </div>

            {/* Filter toggle + count */}
            <div className="mc-filter-bar">
                <button
                    className={`mc-filter-toggle ${showFilters ? 'active' : ''}`}
                    onClick={() => setShowFilters(!showFilters)}
                >
                    <Filter size={14} />
                    Filtres
                    {activeFilterCount > 0 && <span className="mc-filter-badge">{activeFilterCount}</span>}
                </button>
                <button
                    className={`mc-fav-toggle ${filters.favoritesOnly ? 'active' : ''}`}
                    onClick={() => updateFilter('favoritesOnly', !filters.favoritesOnly)}
                >
                    <Star size={14} fill={filters.favoritesOnly ? '#f59e0b' : 'none'} />
                    Favoris
                </button>
                <span className="mc-result-count">{data.length} sur {totalCount} actions</span>
            </div>

            {/* Expanded filters */}
            {showFilters && (
                <div className="mc-filters-panel">
                    <div className="mc-filter-group">
                        <label>Variation</label>
                        <select value={filters.variation} onChange={e => updateFilter('variation', e.target.value as any)}>
                            <option value="all">Toutes</option>
                            <option value="up">📈 Hausses</option>
                            <option value="down">📉 Baisses</option>
                            <option value="unchanged">➖ Inchangées</option>
                        </select>
                    </div>
                    <div className="mc-filter-group">
                        <label>Prix</label>
                        <select value={filters.priceRange} onChange={e => updateFilter('priceRange', e.target.value as any)}>
                            <option value="all">Tous prix</option>
                            <option value="under_2000">&lt; 2 000</option>
                            <option value="2000_10000">2K - 10K</option>
                            <option value="10000_50000">10K - 50K</option>
                            <option value="over_50000">&gt; 50K</option>
                        </select>
                    </div>
                    {sectors.length > 0 && (
                        <div className="mc-filter-group">
                            <label>Secteur</label>
                            <select value={filters.sector} onChange={e => updateFilter('sector', e.target.value)}>
                                <option value="all">Tous</option>
                                {sectors.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                    )}
                    <div className="mc-filter-group">
                        <label>Volume</label>
                        <select value={filters.amountRange} onChange={e => updateFilter('amountRange', e.target.value as any)}>
                            <option value="all">Tous vol.</option>
                            <option value="under_20m">&lt; 20M</option>
                            <option value="20m_50m">20-50M</option>
                            <option value="50m_100m">50-100M</option>
                            <option value="over_100m">&gt; 100M</option>
                        </select>
                    </div>
                </div>
            )}

            {/* Stock list */}
            <div className="mc-stocks-list">
                {data.length === 0 ? (
                    <div className="mc-empty">Aucune action correspondante</div>
                ) : (
                    data.map((stock) => {
                        const isFav = favorites.has(stock.symbol);
                        const isUp = stock.status === 1;
                        const hasImgError = imgErrors.has(stock.symbol);

                        return (
                            <div
                                key={stock.symbol}
                                className="mc-stock-card"
                                onClick={() => navigate(`/data?symbol=${stock.symbol}`)}
                            >
                                {/* Left: logo + info */}
                                <div className="mc-stock-left">
                                    {!hasImgError ? (
                                        <img
                                            src={`/images/societes/${stock.symbol}.png`}
                                            alt={stock.symbol}
                                            className="mc-stock-logo"
                                            onError={() => setImgErrors(prev => new Set(prev).add(stock.symbol))}
                                        />
                                    ) : (
                                        <div className="mc-stock-logo-fallback">
                                            {stock.symbol.substring(0, 3)}
                                        </div>
                                    )}
                                    <div className="mc-stock-info">
                                        <span className="mc-stock-name">{stock.name || stock.symbol}</span>
                                        <span className="mc-stock-symbol">{stock.symbol}</span>
                                    </div>
                                </div>

                                {/* Right: price + variation + fav */}
                                <div className="mc-stock-right">
                                    <div className="mc-stock-data">
                                        <span className="mc-stock-price">{formaterPrix(stock.amount)} {stock.currency}</span>
                                        <span className={`mc-stock-change ${isUp ? 'positive' : 'negative'}`}>
                                            {stock.change}
                                        </span>
                                    </div>
                                    <button
                                        className={`mc-fav-btn ${isFav ? 'active' : ''}`}
                                        onClick={(e) => handleFav(e, stock.symbol, isFav)}
                                        aria-label={isFav ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                                    >
                                        <Star size={16} fill={isFav ? '#f59e0b' : 'none'} color={isFav ? '#f59e0b' : '#94a3b8'} />
                                    </button>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default MobileCapital;
